import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import logger from '../../utils/logger';

const C = {
  green: '#00A884',
  blue: '#2563EB',
  orange: '#F59E0B',
  red: '#DC2626',
  dark: '#1F2937',
  mid: '#6B7280',
  light: '#9CA3AF',
  greenBg: '#E6F7F1',
  blueBg: '#EFF6FF',
  orangeBg: '#FEF3E2',
  border: '#F3F4F6',
};

const card = {
  background: '#FFFFFF',
  borderRadius: '10px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const formatTimeAgo = (dateStr) => {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 60) return `${Math.max(1, mins)} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr`;
  return `${Math.floor(hrs / 24)}d`;
};

function RingGauge({ percent, color, label }) {
  const r = 46;
  const circ = 2 * Math.PI * r;
  return (
    <Box sx={{ textAlign: 'center' }}>
      <svg width="104" height="104" viewBox="0 0 110 110">
        <circle cx="55" cy="55" r={r} fill="none" stroke={C.border} strokeWidth="9" />
        <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="9"
          strokeLinecap="round" strokeDasharray={`${(percent / 100) * circ} ${circ}`}
          transform="rotate(-90 55 55)" />
        <text x="55" y="61" textAnchor="middle"
          style={{ fontSize: '20px', fontWeight: 700, fill: C.dark }}>{percent}%</text>
      </svg>
      <Box sx={{ fontSize: '13px', fontWeight: 600, color: '#4B5563', mt: '6px' }}>{label}</Box>
    </Box>
  );
}

const BAR_COLORS = [C.green, C.blue, C.orange];
const BAR_KEYS = ['intake_count', 'followup_count', 'pending_count'];
const BAR_LABELS = ['First Intake', 'Followup Form', 'Pending Forms'];

function GroupedBarChart({ data, colors = BAR_COLORS }) {
  const [hover, setHover] = useState(null);
  if (!data?.length) return <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.light, fontSize: '14px' }}>No data for this period</Box>;

  const W = 620, H = 280, PL = 44, PR = 16, PT = 20, PB = 44;
  const ch = H - PT - PB;
  const n = data.length;
  const barsPerGroup = 3;

  const allVals = data.flatMap(d => BAR_KEYS.map(k => parseInt(d[k]) || 0));
  const maxRaw = Math.max(...allVals, 1);
  const step = maxRaw <= 5 ? 1 : maxRaw <= 20 ? 5 : maxRaw <= 50 ? 10 : maxRaw <= 200 ? 50 : Math.ceil(maxRaw / 5 / 100) * 100;
  const maxVal = Math.ceil(maxRaw / step) * step || step;
  const ticks = Array.from({ length: Math.floor(maxVal / step) + 1 }, (_, i) => i * step);
  const Y = v => PT + ch - (v / maxVal) * ch;

  const totalBarArea = W - PL - PR;
  const groupGap = Math.max(12, totalBarArea / n * 0.25);
  const groupW = (totalBarArea - groupGap * (n - 1)) / n;
  const barGap = 2;
  const barW = Math.max(4, Math.min(24, (groupW - barGap * (barsPerGroup - 1)) / barsPerGroup));
  const actualGroupW = barW * barsPerGroup + barGap * (barsPerGroup - 1);
  const offsetX = PL + (totalBarArea - (actualGroupW + groupGap) * n + groupGap) / 2;

  return (
    <Box>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block' }} onMouseLeave={() => setHover(null)}>
        <defs>
          {colors.map((c, i) => (
            <linearGradient key={i} id={`gbar${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c} stopOpacity="0.95" />
              <stop offset="100%" stopColor={c} stopOpacity="0.65" />
            </linearGradient>
          ))}
        </defs>
        {ticks.map(t => {
          const y = Y(t);
          return (
            <g key={t}>
              <line x1={PL} y1={y} x2={W - PR} y2={y} stroke={t === 0 ? '#D1D5DB' : '#F3F4F6'} />
              <text x={PL - 8} y={y + 4} textAnchor="end" fontSize="10" fontWeight="500" fill={C.light}
                style={{ fontVariantNumeric: 'tabular-nums' }}>{t}</text>
            </g>
          );
        })}
        {data.map((d, gi) => {
          const gx = offsetX + gi * (actualGroupW + groupGap);
          const isHov = hover === gi;
          return (
            <g key={gi} onMouseEnter={() => setHover(gi)} onMouseLeave={() => setHover(null)} style={{ cursor: 'pointer' }}>
              <rect x={gx - 4} y={PT} width={actualGroupW + 8} height={ch + PB} fill="transparent" />
              {BAR_KEYS.map((k, bi) => {
                const v = parseInt(d[k]) || 0;
                const barH = Math.max(v > 0 ? 2 : 0, (v / maxVal) * ch);
                const bx = gx + bi * (barW + barGap);
                const by = PT + ch - barH;
                return (
                  <rect key={bi} x={bx} y={by} width={barW} height={barH} rx={Math.min(3, barW / 2)}
                    fill={`url(#gbar${bi})`}
                    style={{ transition: 'all 0.15s ease', filter: isHov ? 'drop-shadow(0 1px 4px rgba(0,0,0,0.18))' : 'none' }} />
                );
              })}
              <text x={gx + actualGroupW / 2} y={H - 8} textAnchor="middle" fontSize="9" fontWeight="600" fill={C.mid}>{d.month}</text>
            </g>
          );
        })}
        {hover !== null && (() => {
          const d = data[hover];
          const gx = offsetX + hover * (actualGroupW + groupGap) + actualGroupW / 2;
          const tw = 160, th = 64;
          let tx = gx - tw / 2;
          if (tx < PL) tx = PL;
          if (tx + tw > W - PR) tx = W - PR - tw;
          return (
            <g>
              <rect x={tx} y={2} width={tw} height={th} rx="8" fill="#1F2937" opacity="0.95" />
              <text x={tx + tw / 2} y={17} textAnchor="middle" fontSize="11" fontWeight="600" fill="#E5E7EB">{d.month}</text>
              {BAR_KEYS.map((k, bi) => (
                <g key={bi}>
                  <circle cx={tx + 12} cy={30 + bi * 14} r={4} fill={colors[bi]} />
                  <text x={tx + 22} y={34 + bi * 14} fontSize="10" fontWeight="500" fill="#9CA3AF">{BAR_LABELS[bi]}</text>
                  <text x={tx + tw - 10} y={34 + bi * 14} textAnchor="end" fontSize="11" fontWeight="700" fill="#FFFFFF"
                    style={{ fontVariantNumeric: 'tabular-nums' }}>{parseInt(d[k]) || 0}</text>
                </g>
              ))}
            </g>
          );
        })()}
      </svg>
    </Box>
  );
}

const SVG_ICONS = {
  patients: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M23 21v-2a4 4 0 0 0-3-3.87',
  intake: 'M4 7V4h16v3M9 20h6M12 4v16',
  followup: 'M12 22a10 10 0 1 0-10-10M2 12h4M12 8v4l3 3',
  completed: 'M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4 12 14.01l-3-3',
  pending: 'M12 8v4l3 3 M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z',
  sent: 'M22 2L11 13 M22 2l-7 20-4-9-9-4z',
};

export default function DashboardHome({ onNavigate }) {
  const { tenant } = useTenant();
  const primary = tenant?.primaryColor || C.green;
  const palette = { ...C, green: primary, greenBg: primary + '20' };
  const barColors = [primary, C.blue, C.orange];
  const tenantId = tenant?.id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartRange, setChartRange] = useState('today');
  const [rangeStats, setRangeStats] = useState(null);
  const [rangeChart, setRangeChart] = useState(null);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const fmtDate = (iso) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${m}/${d}/${y}`; };

  useEffect(() => {
    const params = tenantId ? `?tenantId=${tenantId}` : '';
    api.get(`/api/admin/dashboard-stats${params}`)
      .then(res => { setData(res.data); })
      .catch(err => logger.error('Dashboard stats error:', err))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId) return;
    if (chartRange === 'total') {
      setRangeStats(null);
      setRangeChart(null);
      return;
    }
    if (chartRange === 'custom') return;
    setRangeLoading(true);
    api.get(`/api/admin/dashboard-stats?tenantId=${tenantId}&chartRange=${chartRange}`)
      .then(res => {
        if (res.data.stats) setRangeStats(res.data.stats);
        if (res.data.monthlySubmissions) setRangeChart(res.data.monthlySubmissions);
      })
      .catch(err => logger.error('Range stats error:', err))
      .finally(() => setRangeLoading(false));
  }, [tenantId, chartRange]);

  const handleRangeChange = (range) => {
    if (range === 'total') {
      setRangeStats(null);
      setRangeChart(null);
    }
    setChartRange(range);
  };

  const handleCustomApply = () => {
    if (!customStart || !customEnd) return;
    setRangeLoading(true);
    api.get(`/api/admin/dashboard-stats?tenantId=${tenantId}&chartRange=custom&chartStart=${customStart}&chartEnd=${customEnd}`)
      .then(res => {
        if (res.data.stats) setRangeStats(res.data.stats);
        if (res.data.monthlySubmissions) setRangeChart(res.data.monthlySubmissions);
      })
      .catch(err => logger.error('Custom range error:', err))
      .finally(() => setRangeLoading(false));
  };

  const handleRefresh = () => {
    setLoading(true);
    setRangeStats(null);
    setRangeChart(null);
    setChartRange('today');
    const params = tenantId ? `?tenantId=${tenantId}` : '';
    api.get(`/api/admin/dashboard-stats${params}`)
      .then(res => { setData(res.data); })
      .catch(err => logger.error('Dashboard stats error:', err))
      .finally(() => setLoading(false));
  };

  const RANGE_LABELS = { total: 'Total', today: 'Today', weekly: 'This Week', monthly: 'This Month', custom: 'Custom Range' };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: palette.green }} />
      </Box>
    );
  }

  if (!data?.success) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography color="error">Failed to load dashboard data</Typography>
      </Box>
    );
  }

  const { recentActivity } = data;
  const stats = rangeStats || data.stats;
  const chartData = rangeChart || data.monthlySubmissions || [];

  const tc = stats.tagCounts || {};
  const kpis = [
    { label: 'Total Patients', value: stats.totalPatients, icon: SVG_ICONS.patients,
      iconBg: palette.greenBg, iconColor: palette.green, sub: `${stats.activePatients} active`, subColor: palette.green, filterKey: 'all' },
    { label: 'Intake Pending', value: tc['intake-pending'] ?? 0, icon: SVG_ICONS.pending,
      iconBg: '#fef3c7', iconColor: '#f59e0b', sub: 'Started but not completed', subColor: '#f59e0b', filterKey: 'intake-pending' },
    { label: 'Intake Complete', value: tc['intake-complete'] ?? 0, icon: SVG_ICONS.completed,
      iconBg: '#dcfce7', iconColor: '#16a34a', sub: 'First visit completed', subColor: '#16a34a', filterKey: 'intake-complete' },
    { label: 'F/U Sent', value: tc['followup-sent'] ?? 0, icon: SVG_ICONS.sent,
      iconBg: '#fef3c7', iconColor: '#d97706', sub: 'Follow-up email sent', subColor: '#d97706', filterKey: 'followup-sent' },
    { label: 'F/U Pending', value: tc['followup-pending'] ?? 0, icon: SVG_ICONS.followup,
      iconBg: '#ffedd5', iconColor: '#f97316', sub: 'Opened or awaiting response', subColor: '#f97316', filterKey: 'followup-pending' },
    { label: 'F/U Complete', value: tc['followup-complete'] ?? 0, icon: SVG_ICONS.completed,
      iconBg: '#dbeafe', iconColor: '#2563eb', sub: 'Follow-up submitted', subColor: '#2563eb', filterKey: 'followup-complete' },
  ];

  const activityItems = (recentActivity || []).map(item => {
    const name = `${item.patient_first_name} ${item.patient_last_name}`;
    if (item.status === 'pending') {
      return { text: 'Form started — awaiting completion', patient: name,
        badge: 'Pending', dot: C.orange, badgeFg: C.orange, badgeBg: C.orangeBg, time: formatTimeAgo(item.created_at) };
    }
    if (item.follow_up_count > 0) {
      return { text: 'Follow-up form submitted', patient: `${name} · visit ${item.follow_up_count}`,
        badge: 'Done', dot: palette.green, badgeFg: palette.green, badgeBg: palette.greenBg, time: formatTimeAgo(item.created_at) };
    }
    return { text: 'Intake form completed', patient: `${name} · first visit`,
      badge: 'New', dot: C.blue, badgeFg: C.blue, badgeBg: C.blueBg, time: formatTimeAgo(item.created_at) };
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
      {/* Dashboard Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={palette.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
          </svg>
          <Box sx={{ fontSize: '22px', fontWeight: 700, color: C.dark }}>Dashboard</Box>
        </Box>
        <Box onClick={handleRefresh} sx={{ width: 36, height: 36, borderRadius: '8px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { background: '#F3F4F6' } }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.mid} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6" /><path d="M1 20v-6h6" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
          </svg>
        </Box>
      </Box>

      {/* Range Filter Buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {['today', 'weekly', 'monthly', 'total', 'custom'].map(r => (
          <Box key={r} component="span" onClick={() => handleRangeChange(r)}
            sx={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, borderRadius: '20px',
              cursor: 'pointer', transition: 'all 0.15s', userSelect: 'none',
              ...(chartRange === r
                ? { background: palette.green, color: '#FFFFFF', border: `1.5px solid ${palette.green}` }
                : { background: '#FFFFFF', color: C.mid, border: '1.5px solid #E5E7EB',
                    '&:hover': { borderColor: palette.green, color: palette.green } }
              ),
            }}>{RANGE_LABELS[r]}</Box>
        ))}
      </Box>

      {/* Custom Date Range */}
      {chartRange === 'custom' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Box sx={{ position: 'relative' }}>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'inherit', color: 'transparent', width: '140px', background: '#fff' }} />
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: customStart ? C.dark : C.light, pointerEvents: 'none' }}>
              {customStart ? fmtDate(customStart) : 'MM/DD/YYYY'}
            </span>
          </Box>
          <span style={{ color: C.mid, fontSize: '13px' }}>to</span>
          <Box sx={{ position: 'relative' }}>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              style={{ padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', outline: 'none', fontFamily: 'inherit', color: 'transparent', width: '140px', background: '#fff' }} />
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px', color: customEnd ? C.dark : C.light, pointerEvents: 'none' }}>
              {customEnd ? fmtDate(customEnd) : 'MM/DD/YYYY'}
            </span>
          </Box>
          <Box component="button" onClick={handleCustomApply}
            sx={{ padding: '8px 20px', fontSize: '13px', fontWeight: 600, borderRadius: '8px', border: 'none',
              background: palette.green, color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.9 } }}>Apply</Box>
        </Box>
      )}

      {/* Loading overlay for range change */}
      {rangeLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress sx={{ color: palette.green }} />
        </Box>
      )}

      {/* KPI Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: '18px', ...(rangeLoading && { display: 'none' }) }}>
        {kpis.map((kpi, i) => (
          <Box key={i} onClick={() => onNavigate?.(kpi.filterKey, { range: chartRange, ...(chartRange === 'custom' && { startDate: customStart, endDate: customEnd }) })} sx={{ ...card, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px',
            cursor: 'pointer', '&:hover': { boxShadow: '0 4px 10px rgba(0,0,0,0.10)' } }}>
            <Box sx={{ width: 46, height: 46, borderRadius: '10px', background: kpi.iconBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={kpi.iconColor}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={kpi.icon} /></svg>
            </Box>
            <Box>
              <Box sx={{ fontSize: '13px', fontWeight: 500, color: C.mid }}>{kpi.label}</Box>
              <Box sx={{ fontSize: '26px', fontWeight: 700, color: C.dark, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>
                {kpi.value}
              </Box>
              <Box sx={{ fontSize: '12px', fontWeight: 500, color: kpi.subColor }}>{kpi.sub}</Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Submissions Chart + Completion Rates */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '18px', ...(rangeLoading && { display: 'none' }) }}>
        <Box sx={card}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
            <Box sx={{ fontSize: '16px', fontWeight: 700, color: C.dark }}>Submissions</Box>
            <Box sx={{ display: 'flex', gap: '14px' }}>
              {BAR_LABELS.map((label, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: barColors[i] }} />
                  <Box sx={{ fontSize: '12px', color: C.mid, fontWeight: 500 }}>{label}</Box>
                </Box>
              ))}
            </Box>
          </Box>
          <GroupedBarChart data={chartData} colors={barColors} />
        </Box>

        <Box sx={{ ...card, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ fontSize: '16px', fontWeight: 700, color: C.dark }}>Completion Rates</Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', flex: 1, py: '16px' }}>
            <RingGauge percent={stats.intakePercent || 0} color={palette.green} label="Intake" />
            <RingGauge percent={stats.returningPercent || 0} color={C.blue} label="Follow-up" />
          </Box>
          <Box sx={{ borderTop: `1px solid ${C.border}`, pt: '12px', fontSize: '13px', color: C.mid }}>
            {stats.pendingForms ?? 0} pending forms · {stats.completionRate || 0}% overall completion
          </Box>
        </Box>
      </Box>

      {/* Patient Insights + Recent Activity */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '18px', ...(rangeLoading && { display: 'none' }) }}>
        <Box sx={card}>
          <Box sx={{ fontSize: '16px', fontWeight: 700, color: C.dark, mb: '16px' }}>Patient Insights</Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600, color: '#4B5563', mb: '8px' }}>
            <span>First-time · {stats.intakePercent || 0}%</span>
            <span>Returning · {stats.returningPercent || 0}%</span>
          </Box>
          <Box sx={{ display: 'flex', height: '10px', borderRadius: '6px', overflow: 'hidden', gap: '2px' }}>
            <Box sx={{ width: `${stats.intakePercent || 0}%`, background: C.blue }} />
            <Box sx={{ width: `${stats.returningPercent || 0}%`, background: palette.green }} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: C.light, mt: '8px', fontVariantNumeric: 'tabular-nums' }}>
            <span>{stats.intakeCount} patients</span>
            <span>{stats.followupCount} patients</span>
          </Box>

          <Box sx={{ borderTop: `1px solid ${C.border}`, my: '18px' }} />

          <Box sx={{ fontSize: '14px', fontWeight: 700, color: C.dark, mb: '2px' }}>Patient Status</Box>
          <Box sx={{ fontSize: '12px', color: C.light, mb: '12px' }}>Active vs Inactive breakdown</Box>
          {[
            { label: 'Active', count: stats.activePatients, color: palette.green },
            { label: 'Inactive', count: stats.inactivePatients, color: C.orange },
          ].map((bar, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '10px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#4B5563', width: 52, fontVariantNumeric: 'tabular-nums' }}>
                {bar.label}
              </span>
              <Box sx={{ flex: 1, height: 8, background: '#F3F4F6', borderRadius: '6px' }}>
                <Box sx={{ height: 8, borderRadius: '6px', background: bar.color,
                  width: `${stats.totalPatients > 0 ? Math.round((bar.count / stats.totalPatients) * 100) : 0}%` }} />
              </Box>
              <span style={{ fontSize: '12px', color: C.light, width: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {bar.count}
              </span>
            </Box>
          ))}
        </Box>

        <Box sx={card}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '10px' }}>
            <Box sx={{ fontSize: '16px', fontWeight: 700, color: C.dark }}>Recent Activity</Box>
          </Box>
          {activityItems.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4, color: C.light, fontSize: '14px' }}>No recent activity</Box>
          )}
          {activityItems.map((act, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '14px', py: '12px',
              borderBottom: `1px solid ${C.border}` }}>
              <Box sx={{ width: 9, height: 9, borderRadius: '9999px', background: act.dot, flex: '0 0 auto' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontSize: '14px', fontWeight: 600, color: C.dark }}>{act.text}</Box>
                <Box sx={{ fontSize: '12px', color: C.light, mt: '2px' }}>{act.patient}</Box>
              </Box>
              <Box sx={{ fontSize: '11px', fontWeight: 600, color: act.badgeFg, background: act.badgeBg,
                borderRadius: '12px', padding: '3px 10px', whiteSpace: 'nowrap' }}>{act.badge}</Box>
              <Box sx={{ fontSize: '12px', color: C.light, width: 56, textAlign: 'right',
                fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{act.time}</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
